<script lang="ts">
    import { getContext, onDestroy, onMount } from "svelte";
    import { navigate } from "svelte-navigator";
    import type { TradeBrand } from "../model";
    import { api } from "../stores";
    import { fly } from "svelte/transition";

    const { close } = getContext("simple-modal");

    /**
     * Time to pass to next brand
     */
    const TIME_TO_GO_NEXT = 5000;

    /**
     * list of brands for popup
     */
    export let tradebrands: TradeBrand[] = [];

    /**
     * index of current brand displayed
     */
    let index = 0;

    /**
     * interval id of interval to change displayed brand
     */
    let intervalID;

    /**
     * element brand displayed
     */
    let brandElement;

    /**
     * coordonate of mouse down container
     */
    let translateContainerCoordonate: { x: number; y: number };

    onMount(() => {
        createInterval();
    });

    onDestroy(() => {
        deleteInterval();
    });

    /**
     * create interval to change brand displayed
     */
    function createInterval() {
        if (tradebrands && tradebrands.length > 1)
            intervalID = setInterval(() => goToNextIndex(), TIME_TO_GO_NEXT);
    }

    /**
     * remove interval of change brand
     */
    function deleteInterval() {
        if (intervalID) {
            clearInterval(intervalID);
        }
    }

    /**
     * recreate interval
     */
    function recreateInterval() {
        deleteInterval();
        createInterval();
    }

    /**
     * got to next index
     */
    function goToNextIndex() {
        let newIndex = index + 1;
        if (newIndex >= tradebrands.length) {
            newIndex = 0;
        }
        changeTrade(newIndex);
    }

    /**
     * go to previous index
     */
    function goToPreviousIndex() {
        let newIndex = index - 1;
        if (newIndex < 0) {
            newIndex = tradebrands.length - 1;
        }
        changeTrade(newIndex);
    }

    /**
     * go to brands products list
     * @param trade : trade to go
     */
    function goToTradeBrand(trade: string) {
        deleteInterval();
        close();
        navigate(
            `/catalog/${$api.getOperationCode()}/${$api.getShopId()}/products?brand=${trade}`
        );
    }

    /**
     * change display brand
     * @param i : brand index in array
     */
    function changeTrade(i: number) {
        index = i;
        recreateInterval();
    }

    /**
     * keydown to change brand with left,right arrox
     * @param e
     */
    function handleKeydown(e) {
        if (e.keyCode === 39 && index < tradebrands.length - 1) {
            // arrow right
            goToNextIndex();
        } else if (e.keyCode === 37 && index !== 0) {
            // arrow left
            goToPreviousIndex();
        }
    }

    /**
     * listen mouse down in image
     * @param event: Event to get mouse down
     */
    function handleMouseDownPage(event) {
        // get initial container coordonate
        translateContainerCoordonate = {
            x: event.clientX,
            y: event.clientY,
        };

        deleteInterval();
    }

    /**
     * listen mouse move in image
     * @param event
     */
    function handleMouseMovePage(event) {
        if (translateContainerCoordonate) {
            // move container
            let diff = translateContainerCoordonate.x - event.clientX;
            translateTransformElement(diff);
        }
    }

    /**
     * when user terminate to swipe stop
     */
    function handleMouseUpPage(event) {
        let endCoordonate = {
            x: event.clientX,
            y: event.clientY,
        };
        if (
            translateContainerCoordonate &&
            translateContainerCoordonate.x != endCoordonate.x
        ) {
            // change brand
            let diff = translateContainerCoordonate.x - endCoordonate.x;
            if (diff > 0) {
                // next page
                goToNextIndex();
            } else {
                // previous page
                goToPreviousIndex();
            }

            // reset events values
            translateTransformElement(0);
            translateContainerCoordonate = null;
        }
    }

    /**
     * handle mouse leave to reset element
     * @param event
     */
    function handleMouseLeave(event): void {
        if (translateContainerCoordonate) {
            // reset events values
            translateTransformElement(0);
            translateContainerCoordonate = null;
            createInterval();
        }
    }

    /**
     * translate container
     * @param value
     */
    function translateTransformElement(value: number): void {
        brandElement.style.transform = `translateX(${value * -1}px)`;
    }
</script>

<svelte:window on:keydown={handleKeydown} />

{#if tradebrands && tradebrands.length > 0}
    <div class="brand-carousel">
        <!-- Close button -->
        <div
            class=" close is-clickable"
            on:click={() => {
                deleteInterval();
                close();
            }}
        >
            <i class="close-icon fas fa-times fa-2x" />
        </div>

        <!-- Brand displayed -->
        {#each [tradebrands[index]] as trade (index)}
            <div
                class="trade"
                bind:this={brandElement}
                in:fly={{ x: -30, duration: 100 }}
                title={`Aller sur l'espace marque de ${trade.name}`}
                on:click={() => goToTradeBrand(trade.name)}
                on:mousedown={handleMouseDownPage}
                on:mouseup={handleMouseUpPage}
                on:mousemove={handleMouseMovePage}
                on:mouseleave={handleMouseLeave}
                style="background-image: url({trade.popup_url});"
            />
        {/each}

        <!-- Button to change brand -->
        <!-- <button
            class="button-change previous"
            on:click={() => goToPreviousIndex()}
        >
            <i class="fas fa-chevron-left fa-2x" />
        </button>
        <button class="button-change next" on:click={() => goToNextIndex()}>
            <i class="fas fa-chevron-right fa-2x" />
        </button> -->

        <!-- carousel nav -->
        <nav class="carousel-nav">
            {#each tradebrands as trade, i}
                <input
                    id={`carousel-nav-item-${i}`}
                    type="radio"
                    name="carousel-nav-dots"
                    on:change={() => changeTrade(i)}
                    checked={index == i}
                />
                <label for={`carousel-nav-item-${i}`}>
                    Aller sur le marque {trade.name}</label
                >
            {/each}
        </nav>
    </div>
{/if}

<style lang="scss">
    @import "../styles/variables.scss";

    .brand-carousel {
        position: relative;
        max-width: $popup-width;
        max-height: $popup-height;
        width: 100%;
        height: 100%;
        aspect-ratio: 1 / 1;
        color: white;
        display: inline-block;
        overflow: hidden;

        .close {
            position: absolute;
            right: 0.7rem;
            top: 0px;
            color: $dark-black;
        }
        .close-button {
            display: flex;
            position: absolute;
            right: 0px;
            top: 0px;
            height: 40px;
            width: 40px;
            background-color: white;
            color: $dark-black;
            border-radius: 9999px;
            // border-color: $dark-black;
            border: 3px solid $dark-black;
            z-index: 15;
            .close-icon {
                margin: auto;
            }

            &:hover {
                background-color: $dark-black;
                color: white;
            }
        }

        .trade {
            height: 100%;
            width: 100%;
            border-radius: 12px;
            background-position: center; /* Center the image */
            background-repeat: no-repeat; /* Do not repeat the image */
            background-size: cover; /* Resize the background image to cover the entire container */

            cursor: pointer;
        }

        .button-change {
            display: inline-block;
            position: absolute;
            top: 46%;
            z-index: 10;
            border: transparent;
            background-color: transparent;
            cursor: pointer;
            &.next {
                right: 1px;
            }
            &.previous {
                left: 1px;
            }

            &:hover {
                color: $dark-black;
            }
        }
    }

    nav.carousel-nav {
        position: absolute;
        bottom: 1rem;
        width: 100%;
        text-align: center;
    }

    nav.carousel-nav:hover {
        cursor: default;
    }

    /* Hide the radio button */
    nav.carousel-nav input[type="radio"] {
        display: none;
    }

    /* All styling takes place on the label element */
    nav.carousel-nav label {
        display: inline-block;
        background: #ddd;
        overflow: hidden;
        text-indent: -999px;
        border-radius: 100%;
        width: 15px;
        height: 15px;
        box-shadow: inset 0 1px 1px 0 #999;
    }
    nav.carousel-nav label:hover {
        background: #bbb;
        cursor: pointer;
        box-shadow: inset 0 1px 1px 0 #777;
    }
    nav.carousel-nav input:checked + label {
        background: $dark-black;
        box-shadow: inset 0 0 1px 1px $dark-black;
    }
</style>
